import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, takeWhile, tap, throwError, timer} from 'rxjs';
import {catchError, finalize, map, switchMap} from 'rxjs/operators';
import {TtdPriceModel} from '../distribution-account/distribution-account.model';
import {
  ActiveAgentAttribute,
  ActiveAgentService
} from '../segment/info/shared-targets-list/active-agent/active-agent.service';
import {License} from '../segment/info/shared-targets-list/account-google-ddp/domain';
import {TargetService} from '../target/target.service';
import {NotificationService} from '../utils/notification-service';
import {XandrService} from '../xandr/xandr.service';
import {XandrBuyer, XandrSegmentBillingRequest} from '../xandr/xandr.types';
import {AccountConfigurationWrapper, Share, ShareAccount, ShareResult} from './domain';
import {Store} from '@ngrx/store';
import {ChangedPubmaticBuyerDTO} from '../segment/info/shared-targets-list/account-pubmatic/pubmatic-state';
import {multishareComplete} from '../segment/segments.state';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AdditionalSharingConfigBase {
}

export enum TargetSystem {
  ACTIVE_AGENT = 'activeAgent',
  XANDR = 'xandr',
  PUBMATIC = 'pubmatic',
  GOOGLE = 'google',
  THE_TRADE_DESK = 'theTradeDesk'
}

export type AdditionalSharingConfig = {
  [key in TargetSystem as string]: AdditionalSharingConfigBase;
};

export interface PubmaticShareConfig extends AdditionalSharingConfigBase {
  price: string;
  buyers: ChangedPubmaticBuyerDTO[];
}

export interface GoogleShareConfig extends AdditionalSharingConfigBase {
  licensees: License[];
}

export interface ActiveAgentShareConfig extends AdditionalSharingConfigBase {
  attributes: ActiveAgentAttribute[];
}

export interface XandrShareConfig extends AdditionalSharingConfigBase {
  buyers: XandrBuyer[];
  publishers: XandrBuyer[];
  billing: XandrSegmentBillingRequest;
}

export interface TheTradeDeskShareConfig extends AdditionalSharingConfigBase {
  categoryId: string;
  pricings: TtdPriceModel[];
  names: { [key: number]: string };
}

@Injectable({
  providedIn: 'root'
})
export class SegmentShareService {

  private selectedSegmentsSource = new BehaviorSubject<number[]>([]);
  selectedSegments = this.selectedSegmentsSource.asObservable();
  private selectedTargetAccountsSource = new BehaviorSubject<number[]>([]);
  selectedTargetAccounts = this.selectedTargetAccountsSource.asObservable();
  private accountConfigsSource = new BehaviorSubject(new Map<number, AccountConfigurationWrapper>());

  constructor(
    private targetService: TargetService,
    private activeAgentService: ActiveAgentService,
    private xandrService: XandrService,
    private notificationService: NotificationService,
    private store: Store
  ) {
  }

  addSegmentToSelection(segmentId: number) {
    const currEntries = this.selectedSegmentsSource.getValue();
    if (!currEntries.includes(segmentId)) {
      this.selectedSegmentsSource.next([...currEntries, segmentId]);
    }
  }

  removeSegmentFromSelection(segmentId: number) {
    const newSelections = this.selectedSegmentsSource.getValue().filter((id) => id !== segmentId);
    this.selectedSegmentsSource.next(newSelections);
  }

  addAccountToSelection(accountId: number) {
    const currEntries = this.selectedTargetAccountsSource.getValue();
    if (!currEntries.includes(accountId)) {
      this.selectedTargetAccountsSource.next([...currEntries, accountId]);
    }
  }

  removeAccountFromSelection(accountId: number) {
    const newSelections = this.selectedTargetAccountsSource.getValue().filter((id) => id !== accountId);
    this.selectedTargetAccountsSource.next(newSelections);
  }

  addAccountDetailConfiguration(targetSystem: TargetSystem, accountId: number, config: AdditionalSharingConfigBase) {
    const newConfigs = new Map(this.accountConfigsSource.getValue());
    newConfigs.set(accountId, {
      accountId,
      config: {[targetSystem]: config} as AdditionalSharingConfig
    } as AccountConfigurationWrapper);
    this.accountConfigsSource.next(newConfigs);
  }

  bulkOperation(bulkShareMode: 'SHARE' | 'UNSHARE'): Observable<{
    shareResults: ShareResult[],
    expectedShareResultsCount: number
  }> {
    const accountConfigs = this.accountConfigsSource.getValue();
    const selectedSegments = this.selectedSegmentsSource.getValue();
    const targetAccounts = this.selectedTargetAccountsSource.getValue();

    const accounts = targetAccounts.map(accountId =>
      ({distributionId: accountId, additionalSharingConfiguration: accountConfigs.get(accountId)?.config})
    ) as ShareAccount[];
    return this.performBulkOperation(bulkShareMode, selectedSegments, accounts).pipe(
      tap(() => this.notificationService.success(`Segments bulk operation successfully scheduled.`)),
      switchMap((id: string) =>
        this.pollForResults(id)
          .pipe(
            takeWhile(share => share.state !== 'FINISHED', true),
            map(result => ({
              shareResults: result.shareResults,
              expectedShareResultsCount: selectedSegments.length * targetAccounts.length
            }))
          )),
      catchError((error) => {
        this.notificationService.error('Error occurred during multi-segment share');
        console.error(error.error);
        return throwError(error.error);
      }),
      finalize(() => {
        this.store.dispatch(multishareComplete({segmentIds: selectedSegments}));
        this.reset();
      })
    );
  }

  reset() {
    this.selectedSegmentsSource.next([]);
    this.selectedTargetAccountsSource.next([]);
    this.accountConfigsSource.next(new Map());
    this.xandrService.buyers.next(new Map());
    this.xandrService.publishers.next(new Map());
  }

  private performBulkOperation(bulkShareMode: 'SHARE' | 'UNSHARE',
                               selectedSegments: number[],
                               accounts: ShareAccount[]): Observable<string> {
    if (bulkShareMode === 'SHARE') {
      return this.targetService.shareMultipleSegmentsToTargets(selectedSegments, accounts);
    } else {
      return this.targetService.bulkUnshare(selectedSegments, accounts);
    }
  }

  private pollForResults(id: string, pollIntervalMillis = 1000): Observable<Share> {
    return timer(0, pollIntervalMillis)
      .pipe(
        switchMap(() => this.targetService.getMultishareResultsById(id))
      );
  }
}
